import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../navbar";
import { useAuth } from "../../auth/AuthContext";
import GenericDataTable from "./GenericDataTable";
import {
  fetchDayEndCloses,
  fetchDayEndReport,
  finalizeDayEndClose,
} from "../../services/dayEndAuditApi";
import "../Details.css";
import "../Settings/Settings.css";
import "./DayEndAudit.css";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function isoDateDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function money(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function escapeCsv(value) {
  const s = value === null || value === undefined ? "" : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadBlob(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function reportToCsv(report) {
  const lines = ["Section,Field,Value"];
  const push = (section, field, value) => {
    lines.push([section, field, value].map(escapeCsv).join(","));
  };

  push("Header", "Business Date", report.businessDate);
  push("Header", "Generated At", report.generatedAt);
  push("Header", "Is Closed", report.isClosed ? "yes" : "no");

  Object.entries(report.sales || {}).forEach(([k, v]) => push("Sales", k, v));
  Object.entries(report.cashReconciliation || {}).forEach(([k, v]) => {
    if (k !== "formula") push("Cash Reconciliation", k, v);
  });

  (report.tenders || []).forEach((t) => {
    push(
      "Tender",
      t.label,
      `count=${t.settlementCount}; amount=${t.amount}; tips=${t.tips}; total=${t.total}`
    );
  });

  lines.push("");
  lines.push(
    "Settlements,id,tabId,channel,customer,table,amount,tip,total,waiter,payment,time,guests"
  );
  (report.settlements || []).forEach((s) => {
    lines.push(
      [
        "Settlement",
        s.id,
        s.tabId,
        s.channel,
        s.customer,
        s.tableNumber,
        s.amount,
        s.tipAmount,
        s.total,
        s.waiter,
        s.paymentLabel,
        s.time,
        s.guestCount,
      ]
        .map(escapeCsv)
        .join(",")
    );
  });

  return lines.join("\n");
}

function Kpi({ label, value, tone }) {
  const cls = ["dea-kpi", tone === "warn" ? "dea-kpi-warn" : "", tone === "danger" ? "dea-kpi-danger" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls}>
      <span className="dea-kpi-label">{label}</span>
      <span className="dea-kpi-value">{value}</span>
    </div>
  );
}

export default function DayEndAudit() {
  const pageTitle = "Day-End Financial Audit";
  const { tenantServerUrl, accessToken, selectedRestaurantId, user } = useAuth();

  const [businessDate, setBusinessDate] = useState(() => todayIso());
  const [startingFloat, setStartingFloat] = useState("0");
  const [paidOuts, setPaidOuts] = useState("0");
  const [declaredCash, setDeclaredCash] = useState("");
  const [notes, setNotes] = useState("");
  const [forceReclose, setForceReclose] = useState(false);

  const [report, setReport] = useState(null);
  const [closes, setCloses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadReport = useCallback(
    async ({ syncCloseFields = false } = {}) => {
      if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;

      try {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const data = await fetchDayEndReport({
          tenantServerUrl,
          accessToken,
          restaurantId: selectedRestaurantId,
          businessDate,
          startingFloat,
          paidOuts,
          declaredCash: declaredCash === "" ? undefined : declaredCash,
        });

        setReport(data);

        // Sync cash fields from an existing close when switching to a closed day
        if (syncCloseFields && data?.close) {
          setStartingFloat(String(data.close.startingFloat ?? 0));
          setPaidOuts(String(data.close.paidOuts ?? 0));
          if (data.close.declaredCash !== null && data.close.declaredCash !== undefined) {
            setDeclaredCash(String(data.close.declaredCash));
          } else {
            setDeclaredCash("");
          }
          setNotes(data.close.notes || "");
        }
      } catch (err) {
        setError(err.message || "Failed to load day-end report");
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [
      accessToken,
      selectedRestaurantId,
      tenantServerUrl,
      businessDate,
      startingFloat,
      paidOuts,
      declaredCash,
    ]
  );

  const loadCloses = useCallback(async () => {
    if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;
    try {
      const data = await fetchDayEndCloses({
        tenantServerUrl,
        accessToken,
        restaurantId: selectedRestaurantId,
        from: isoDateDaysAgo(90),
        to: todayIso(),
        limit: 90,
      });
      setCloses(Array.isArray(data?.rows) ? data.rows : []);
    } catch {
      setCloses([]);
    }
  }, [accessToken, selectedRestaurantId, tenantServerUrl]);

  useEffect(() => {
    // Date / auth change: load with server-side close cash if present
    if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchDayEndReport({
          tenantServerUrl,
          accessToken,
          restaurantId: selectedRestaurantId,
          businessDate,
        });
        if (cancelled) return;
        setReport(data);
        if (data?.close) {
          setStartingFloat(String(data.close.startingFloat ?? 0));
          setPaidOuts(String(data.close.paidOuts ?? 0));
          if (data.close.declaredCash !== null && data.close.declaredCash !== undefined) {
            setDeclaredCash(String(data.close.declaredCash));
          } else {
            setDeclaredCash("");
          }
          setNotes(data.close.notes || "");
        } else {
          setStartingFloat("0");
          setPaidOuts("0");
          setDeclaredCash("");
          setNotes("");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load day-end report");
          setReport(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, selectedRestaurantId, tenantServerUrl, businessDate]);

  useEffect(() => {
    loadCloses();
  }, [loadCloses]);

  const sales = report?.sales;
  const cash = report?.cashReconciliation;
  const exceptions = report?.exceptions;

  const tenderRows = useMemo(
    () =>
      (report?.tenders || []).map((t) => ({
        tender: t.label,
        settlements: t.settlementCount,
        sales: money(t.amount),
        tips: money(t.tips),
        total: money(t.total),
        guests: t.guestCount,
      })),
    [report]
  );

  const waiterRows = useMemo(
    () =>
      (report?.byWaiter || []).map((w) => ({
        waiter: w.waiter,
        settlements: w.settlementCount,
        sales: money(w.amount),
        tips: money(w.tips),
        total: money(w.total),
      })),
    [report]
  );

  const settlementRows = useMemo(
    () =>
      (report?.settlements || []).map((s) => ({
        time: s.time,
        channel: s.channel,
        table: s.tableNumber,
        customer: s.customer,
        amount: money(s.amount),
        tip: money(s.tipAmount),
        total: money(s.total),
        tender: s.paymentLabel,
        waiter: s.waiter,
        guests: s.guestCount,
      })),
    [report]
  );

  const overrideRows = useMemo(
    () =>
      (exceptions?.staffOverrides || []).map((s) => ({
        time: s.time,
        table: s.tableNumber,
        customer: s.customer,
        amount: money(s.amount),
        waiter: s.waiter,
      })),
    [exceptions]
  );

  const openTableRows = useMemo(
    () =>
      (exceptions?.openTables || []).map((t) => ({
        table: t.tableNumber,
        customer: t.customer,
        waiter: t.waiter,
        cost: money(t.cost),
        guests: t.occupants,
        action: t.action,
        settled: t.isBillSettled,
      })),
    [exceptions]
  );

  const closeHistoryRows = useMemo(
    () =>
      closes.map((c) => ({
        date: c.businessDate,
        closedAt: c.closedAt,
        closedBy: c.closedBy,
        grossSales: money(c.grossSales),
        tips: money(c.totalTips),
        overShort: c.overShort === null || c.overShort === undefined ? "—" : money(c.overShort),
        checks: c.checkCount,
        openTables: c.openTableCount,
      })),
    [closes]
  );

  const varianceTone =
    sales && Math.abs(Number(sales.salesVariance) || 0) > 0.01 ? "warn" : undefined;
  const overShortTone =
    cash?.overShort !== null &&
    cash?.overShort !== undefined &&
    Math.abs(Number(cash.overShort) || 0) > 0.01
      ? "danger"
      : undefined;

  const handleExportCsv = () => {
    if (!report) return;
    downloadBlob(
      `day_end_audit_${report.businessDate || businessDate}.csv`,
      reportToCsv(report),
      "text/csv;charset=utf-8"
    );
  };

  const handleExportJson = () => {
    if (!report) return;
    downloadBlob(
      `day_end_audit_${report.businessDate || businessDate}.json`,
      JSON.stringify(report, null, 2),
      "application/json"
    );
  };

  const handleFinalize = async () => {
    if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;
    if (declaredCash === "" || declaredCash === null) {
      setError("Enter declared cash drawer count before closing the day.");
      return;
    }
    if (
      !window.confirm(
        `Finalize day-end close for ${businessDate}? This locks the audit snapshot for the business date.`
      )
    ) {
      return;
    }

    try {
      setClosing(true);
      setError(null);
      setSuccess(null);
      const result = await finalizeDayEndClose({
        tenantServerUrl,
        accessToken,
        restaurantId: selectedRestaurantId,
        payload: {
          businessDate,
          closedBy: user?.email || user?.name || "manager",
          startingFloat: Number(startingFloat) || 0,
          paidOuts: Number(paidOuts) || 0,
          declaredCash: Number(declaredCash),
          notes,
          force: forceReclose,
        },
      });
      setReport(result.report || result);
      setSuccess(result.message || "Day-end close saved.");
      setForceReclose(false);
      await loadCloses();
    } catch (err) {
      setError(err.message || "Failed to finalize day-end close");
      if (err.body?.close) {
        setReport((prev) =>
          prev
            ? { ...prev, isClosed: true, close: err.body.close }
            : prev
        );
      }
    } finally {
      setClosing(false);
    }
  };

  return (
    <>
      <Navbar pageTitle={pageTitle} />
      <div className="details-main">
        <section className="settings-card dea-card">
          <div className="settings-card-header dea-header">
            <div>
              <h2 className="settings-card-title">Day-end financial audit</h2>
              <p className="settings-main-label">
                Restaurant closing report: sales, tender mix, tips, cash drawer
                reconciliation, open tables, and staff overrides. Export for
                accounting or finalize as the day&apos;s Z-style close.
              </p>
              {report && (
                <span
                  className={`dea-badge ${
                    report.isClosed ? "dea-badge-closed" : "dea-badge-open"
                  }`}
                >
                  {report.isClosed ? "Day closed" : "Day open"}
                </span>
              )}
            </div>
            <Link to="/data" className="tm-btn tm-btn-light">
              Back to Data
            </Link>
          </div>

          <div className="dea-filters">
            <label className="dea-filter">
              <span>Business date</span>
              <input
                className="settings-input"
                type="date"
                value={businessDate}
                onChange={(e) => {
                  setBusinessDate(e.target.value);
                  setForceReclose(false);
                }}
              />
            </label>
            <label className="dea-filter">
              <span>Starting float</span>
              <input
                className="settings-input"
                type="number"
                step="0.01"
                min="0"
                value={startingFloat}
                onChange={(e) => setStartingFloat(e.target.value)}
              />
            </label>
            <label className="dea-filter">
              <span>Paid outs</span>
              <input
                className="settings-input"
                type="number"
                step="0.01"
                min="0"
                value={paidOuts}
                onChange={(e) => setPaidOuts(e.target.value)}
              />
            </label>
            <label className="dea-filter">
              <span>Declared cash count</span>
              <input
                className="settings-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="Drawer count"
                value={declaredCash}
                onChange={(e) => setDeclaredCash(e.target.value)}
              />
            </label>
          </div>

          <div className="dea-actions">
            <button
              type="button"
              className="tm-btn tm-btn-primary"
              onClick={() => loadReport()}
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh report"}
            </button>
            <button
              type="button"
              className="tm-btn tm-btn-dark"
              onClick={handleExportCsv}
              disabled={!report}
            >
              Export CSV
            </button>
            <button
              type="button"
              className="tm-btn tm-btn-light"
              onClick={handleExportJson}
              disabled={!report}
            >
              Export JSON
            </button>
          </div>

          {error && (
            <div className="settings-field">
              <span className="settings-value settings-error">{error}</span>
            </div>
          )}
          {success && (
            <div className="settings-field">
              <span className="settings-value settings-success">{success}</span>
            </div>
          )}

          {loading && !report ? (
            <div className="settings-field">
              <span className="settings-value">Building day-end report…</span>
            </div>
          ) : report ? (
            <>
              <div className="dea-section">
                <h3 className="dea-section-title">Sales summary</h3>
                <div className="dea-kpi-grid">
                  <Kpi label="Gross sales" value={money(sales?.grossSales)} />
                  <Kpi label="Tips" value={money(sales?.totalTips)} />
                  <Kpi label="Total collected" value={money(sales?.totalCollected)} />
                  <Kpi label="Checks" value={sales?.checkCount ?? 0} />
                  <Kpi label="Guests" value={sales?.guestCount ?? 0} />
                  <Kpi label="Avg check" value={money(sales?.averageCheck)} />
                  <Kpi
                    label="Order history sales"
                    value={money(sales?.orderHistorySales)}
                  />
                  <Kpi
                    label="Sales variance"
                    value={money(sales?.salesVariance)}
                    tone={varianceTone}
                  />
                </div>
                <p className="dea-section-note">
                  Variance = settlement gross sales − order history sales. Investigate
                  large gaps before closing.
                </p>
              </div>

              <div className="dea-section">
                <h3 className="dea-section-title">Cash drawer reconciliation</h3>
                <p className="dea-section-note">
                  Expected cash = starting float + cash sales + cash tips − paid outs.
                  Over/short = declared count − expected.
                </p>
                <div className="dea-kpi-grid">
                  <Kpi label="Cash sales" value={money(cash?.cashSales)} />
                  <Kpi label="Cash tips" value={money(cash?.cashTips)} />
                  <Kpi label="Expected cash" value={money(cash?.expectedCash)} />
                  <Kpi
                    label="Declared cash"
                    value={
                      cash?.declaredCash === null || cash?.declaredCash === undefined
                        ? "—"
                        : money(cash.declaredCash)
                    }
                  />
                  <Kpi
                    label="Over / short"
                    value={
                      cash?.overShort === null || cash?.overShort === undefined
                        ? "—"
                        : money(cash.overShort)
                    }
                    tone={overShortTone}
                  />
                  <Kpi
                    label="Open tables"
                    value={exceptions?.openTableCount ?? 0}
                    tone={
                      (exceptions?.openTableCount || 0) > 0 ? "warn" : undefined
                    }
                  />
                  <Kpi
                    label="Staff overrides"
                    value={exceptions?.staffOverrideCount ?? 0}
                    tone={
                      (exceptions?.staffOverrideCount || 0) > 0 ? "warn" : undefined
                    }
                  />
                  <Kpi
                    label="Gateway errors"
                    value={exceptions?.gateway?.error ?? 0}
                    tone={
                      (exceptions?.gateway?.error || 0) > 0 ? "danger" : undefined
                    }
                  />
                </div>
              </div>

              <div className="dea-section">
                <h3 className="dea-section-title">Tender breakdown</h3>
                <GenericDataTable rows={tenderRows} defaultSortKey="tender" />
              </div>

              <div className="dea-section">
                <h3 className="dea-section-title">By server / waiter</h3>
                <GenericDataTable rows={waiterRows} defaultSortKey="total" defaultSortDirection="desc" />
              </div>

              {(overrideRows.length > 0 || openTableRows.length > 0) && (
                <div className="dea-section">
                  <h3 className="dea-section-title">Exceptions</h3>
                  {overrideRows.length > 0 && (
                    <>
                      <p className="dea-section-note">Staff overrides / no payment</p>
                      <GenericDataTable rows={overrideRows} defaultSortKey="time" />
                    </>
                  )}
                  {openTableRows.length > 0 && (
                    <>
                      <p className="dea-section-note">Open / unsettled tables at report time</p>
                      <GenericDataTable rows={openTableRows} defaultSortKey="table" />
                    </>
                  )}
                </div>
              )}

              <div className="dea-section">
                <h3 className="dea-section-title">Settlement detail</h3>
                <GenericDataTable
                  rows={settlementRows}
                  defaultSortKey="time"
                  defaultSortDirection="asc"
                />
              </div>

              <div className="dea-section">
                <h3 className="dea-section-title">Finalize close</h3>
                <div className="dea-close-panel">
                  <label className="dea-cash-field" style={{ width: "100%" }}>
                    <span>Manager notes</span>
                    <textarea
                      className="settings-textarea dea-notes"
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Drawer notes, variance explanation, deposit bag #…"
                    />
                  </label>

                  {report.isClosed && (
                    <label className="settings-payment-enable" style={{ marginTop: 10 }}>
                      <input
                        type="checkbox"
                        checked={forceReclose}
                        onChange={(e) => setForceReclose(e.target.checked)}
                      />
                      <span className="settings-payment-code">
                        Force re-close (overwrite existing snapshot)
                      </span>
                    </label>
                  )}

                  <div className="dea-actions" style={{ marginBottom: 0 }}>
                    <button
                      type="button"
                      className="tm-btn tm-btn-primary"
                      onClick={handleFinalize}
                      disabled={closing || (report.isClosed && !forceReclose)}
                    >
                      {closing
                        ? "Saving…"
                        : report.isClosed
                          ? "Re-close day"
                          : "Finalize day-end close"}
                    </button>
                  </div>

                  {report.close && (
                    <p className="dea-close-meta">
                      Last closed {report.close.closedAt} by {report.close.closedBy}
                      {report.close.overShort !== null &&
                        report.close.overShort !== undefined && (
                          <>
                            {" "}
                            — over/short{" "}
                            <span
                              className={
                                Math.abs(Number(report.close.overShort)) > 0.01
                                  ? "dea-variance-bad"
                                  : "dea-variance-ok"
                              }
                            >
                              {money(report.close.overShort)}
                            </span>
                          </>
                        )}
                    </p>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </section>

        <section className="settings-card dea-card" style={{ marginTop: 16 }}>
          <div className="settings-card-header">
            <h2 className="settings-card-title">Recent day-end closes</h2>
          </div>
          <p className="settings-main-label">
            Finalized Z-style snapshots for the last 90 days. Select a date above to
            reopen the full audit.
          </p>
          <GenericDataTable
            rows={closeHistoryRows}
            defaultSortKey="date"
            defaultSortDirection="desc"
          />
        </section>
      </div>
    </>
  );
}
